import type { TransportServices } from './types'

/**
 * Static implementation of TransportServices
 * Returns pre-fetched data instead of making API calls.
 * Used in read-only mode (public app) where data is
 * already available from the server response.
 */
export class StaticTransportServices implements TransportServices {
  constructor(private data: any) {}

  async calculateTransportScores() {
    return (
      this.data?.scores || {
        overall: 0,
        publicTransport: 0,
        roadAccess: 0,
        activeMobility: 0,
        details: {
          ptProximity: 0,
          ptFrequency: 0,
          ptDiversity: 0,
          highwayAccess: 0,
          mainRoadDistance: 0,
          cycling: 0,
          eMobility: 0,
        },
      }
    )
  }

  async getPublicTransportMetrics() {
    return (
      this.data?.publicTransportMetrics || {
        nearestBusStop: null,
        nearestTramStop: null,
        nearestTrainStation: null,
        totalStopsWithin500m: 0,
        totalStopsWithin1km: 0,
        qualityClass: null,
        determiningStop: null,
      }
    )
  }

  async getRoadAccessMetrics() {
    return (
      this.data?.roadAccessMetrics || {
        nearestHighwayAccess: null,
        nearestMainRoad: null,
        distanceToHighway: 0,
        distanceToMainRoad: 0,
        roadTypes: [],
        allHighwayAccess: [],
      }
    )
  }

  async getActiveMobilityMetrics() {
    return (
      this.data?.activeMobilityMetrics || {
        walkabilityScore: 0,
        cyclingInfrastructure: 0,
        nearestEVStation: null,
        cyclingRoutesNearby: 0,
        evStationsNearby: [],
      }
    )
  }

  async getPublicTransportStops() {
    return this.data?.stops || []
  }

  async getMultiModalIsochrones() {
    return this.data?.isochrones || {}
  }

  categorizePublicTransportStops(stops: any[]) {
    return {
      bus: stops.filter((s) => s.type === 'bus'),
      tram: stops.filter((s) => s.type === 'tram'),
      train: stops.filter((s) => s.type === 'train'),
      boat: stops.filter((s) => s.type === 'boat'),
      cable: stops.filter((s) => s.type === 'cable'),
      other: stops.filter(
        (s) => !['bus', 'tram', 'train', 'boat', 'cable'].includes(s.type)
      ),
    }
  }

  formatScore(score: number) {
    if (score >= 8) return { label: 'Excellent', color: 'success' }
    if (score >= 6) return { label: 'Good', color: 'primary' }
    if (score >= 4) return { label: 'Fair', color: 'warning' }
    return { label: 'Poor', color: 'error' }
  }

  formatQualityClass(qualityClass: string) {
    const descriptions: Record<string, string> = {
      A: 'Best public transport quality',
      B: 'Very good public transport quality',
      C: 'Good public transport quality',
      D: 'Satisfactory public transport quality',
      E: 'Limited public transport quality',
      F: 'Poor public transport quality',
    }
    return {
      label: qualityClass,
      description: descriptions[qualityClass] || 'Unknown quality',
    }
  }
}
